import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import {
  Accordion,
  AccordionContext,
  Button,
  useAccordionButton,
} from "react-bootstrap";
import shortid from "shortid";
import cx from "classnames";

import { Property } from "../properties";

import { createMixpanelEvent } from "actions/mixpanel";
import { MIXPANEL_BRANDKIT_CREATED } from "constants/mixpanelEvents";

import {
  createEditorBrandComponent,
  createEditorBrandMedia,
  creatingEditorBrandFailed,
  invalidateEditorBrand,
} from "actions/brandKit/brandKit";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { FailureToast, SuccessToast } from "components/Toast";
import { themes } from "constants/themes";
import { EditorContext } from "components/Editor";
import {
  useSelectedWidgetSelector,
} from "components/Editor/hooks/useEditorSession";
import StoryboardContext from "components/Storyboard/context";

import styles from "./brandElements.module.scss";
import { INTERACTIVE_CONTENT_TYPES, PAGE_CONTAIN_STORYBOARDS } from "constants/storyboardType";

const optionFields = [
  {
    label: "Form",
    type: "FORMS",
    error: "Form creation is failed.",
  },
  {
    label: "Section",
    type: "SECTIONS",
    error: "Section creation is failed.",
  },
  {
    label: "Carousel",
    type: "CAROUSELS",
    error: "Carousel creation is failed.",
  },
  {
    label: "Asset Grid",
    type: "ASSET_GRID",
    error: "Asset Grid creation is failed.",
  },
  {
    label: "Menu",
    type: "MENUS",
    error: "Menu creation is failed.",
  },
  {
    label: "Seek",
    type: "SEEK",
    error: "Seek creation is failed.",
  },
  {
    label: "Embed",
    type: "EMBED",
    error: "Embed creation is failed.",
  },
];

/** BUTTON */

const BrandButtonProperty = ({ widget, onCancel, type }) => {
  const dispatch = useDispatch();

  const brandkitPalettes = useSelector((state) =>
    _.get(state, "palette.brandkit.items", [])
  );
  const paletteData = brandkitPalettes.find((item) => item.type === type);
  const brandkitId = _.get(paletteData, "id", null);

  const uid = widget.uid || '';

  const editorBrandKit = useSelector((state) =>
    _.get(state, "brandKit.activated.createBrandElement", {})
  );
  const loading = _.get(editorBrandKit, `${uid}.loading`, false) || false;
  const error = _.get(editorBrandKit, `${uid}.error`, false) || false;
  const success = _.get(editorBrandKit, `${uid}.success`, false) || false;

  const [buttonState, setButtonState] = useState({
    name: widget.name || "Button",
    error: null,
  });

  useEffect(() => {
    if (!success) return;

    toast(
      <SuccessToast
        heading={`Success`}
        description={`Added to the Brand Kit Successfully.`}
      />,
      {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      }
    );

    dispatch(invalidateEditorBrand(uid));
    onCancel();
  }, [success]);

  useEffect(() => {
    if (!error) return;

    toast(
      <FailureToast
        heading={`Failure`}
        description={`Unable to Add it to the Brand Kit.`}
      />,
      {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      }
    );

    dispatch(invalidateEditorBrand(uid));
  }, [error]);

  const handleClose = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleSave = () => {
    const { breakpoint, id, editorSession, ...cleanedWidget } =
      widget;
    let widgetName = buttonState.name || "Button";

    if(buttonState.name?.length < 1) {
      setButtonState((prev) => ({
        ...prev,
        error: "Please enter a name.",
      }));
      return;
    }

    const request = [{
        name: widgetName,
        order: 1,
        widgets: [
          {
            ...cleanedWidget,
            name: widgetName,
            properties: {
              ...cleanedWidget.properties,
              desktop: {
                ...cleanedWidget.properties.desktop,
                layout: {
                  ...cleanedWidget.properties.desktop.layout,
                  transform: {
                    translate: "0px, 0px",
                    rotate: "0deg",
                  },
                },
                animation: null,
                variables: [],
              },
              mobile: {
                ...cleanedWidget.properties.mobile,
                layout: {
                  ...cleanedWidget.properties.mobile.layout,
                  transform: {
                    translate: "0px, 0px",
                    rotate: "0deg",
                  },
                },
                animation: null,
                variables: [],
              },
            },
            parameter: null,
            systemDefined: false,
            parent: "widget_SfVs0utv",
            actions: null,
            uid: `widget_${shortid.generate()}`,
          },
        ],
      }];

    dispatch(createEditorBrandMedia(widget.uid, request, brandkitId, 'BUTTON'));
  };

  return (
    <Property.Dialog
      className={styles.styleDialog}
      width={224}
      target={`#${widget.uid}`}
      title={"Add to Brand Kit"}
      onCancel={handleClose}
    >
      <Property.Body>
        <div className={styles.brandMediaContainer}>
          <Property name='What do you want to call this Element?' className={styles.brandMediaInput}>
            <Property.Input value={buttonState.name}
              onChange={(event) => setButtonState({
                ...buttonState,
                name: event.target.value,
              })}
              placeholder="Button Text"
              error={buttonState.error}
            />
          </Property>
          <div className={styles.actions}>
            <Button
              size="sm"
              className={styles.action}
              variant="secondary"
              onClick={handleClose}
              style={{ borderColor: "#e0e0e0" }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              size="sm"
              className={styles.action}
              variant="primary"
              disabled={loading}
            >
              {loading ? `Saving` : `Save Changes`}
            </Button>
          </div>
        </div>
      </Property.Body>
    </Property.Dialog>
  );
};

/** MEDIA */

const BrandMediaProperty = ({ widget, onCancel }) => {
  const contentType = _.get(widget, "content.contentType", "") || "image/jpeg";
  const uid = _.get(widget, "uid", shortid.generate());

  const brandkitPalettes = useSelector((state) =>
    _.get(state, "palette.brandkit.items", [])
  );
  const editorBrandKit = useSelector((state) =>
    _.get(state, "brandKit.activated.createBrandElement", {})
  );
  const loading = _.get(editorBrandKit, `${uid}.loading`, false) || false;
  const error = _.get(editorBrandKit, `${uid}.error`, false) || false;
  const success = _.get(editorBrandKit, `${uid}.success`, false) || false;

  const paletteData = brandkitPalettes.find((item) => item.type === 'MEDIA');
  const brandkitId = _.get(paletteData, "id", null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!success) return;

    toast(
      <SuccessToast
        heading={`Success`}
        description={`Added to the Brand Kit Successfully.`}
      />,
      {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      }
    );

    dispatch(invalidateEditorBrand(uid));
    onCancel();
  }, [success]);

  useEffect(() => {
    if (!error) return;

    toast(
      <FailureToast
        heading={`Failure`}
        description={`Unable to Add it to the Brand Kit.`}
      />,
      {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      }
    );

    dispatch(invalidateEditorBrand(uid));
  }, [error]);

  const handleComplete = () => {
    const formats = [
      "image/jpg",
      "image/jpeg",
      "image/png",
      "image/svg",
      "image/bmp",
      "image/gif",
      "image/svg+xml",
      "video/mp4",
    ];

    if (formats.includes(contentType)) {
      var request = {
        brandkitId,
        id: widget.content?.id || ''
      };

      handleUploadMedia(request);
    }
  };

  const handleUploadMedia = (request) => {
    dispatch(createEditorBrandMedia(uid, request, brandkitId, 'MEDIA'));

    var mixpanelPayload = {
      brandkit_element_type: "Images",
    };

    if (contentType === "video/mp4") {
      mixpanelPayload = {
        brandkit_element_type: "Videos",
      };
    }

    dispatch(createMixpanelEvent(MIXPANEL_BRANDKIT_CREATED, mixpanelPayload));
  };

  const handleClose = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Property.Dialog
      className={styles.styleDialog}
      width={224}
      target={`#${uid}`}
      title={"Add to Brand Kit"}
      onCancel={handleClose}
    >
      <Property.Body>
        <div className={styles.brandMediaContainer}>
          <div className={styles.brandMediaText}>
            <p>This element will be saved to your Brand Kit for future use.</p>
            <p>Are you sure you want to proceed?</p>
          </div>
          <div className={styles.actions}>
            <Button
              size="sm"
              className={styles.action}
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
              style={{ borderColor: "#e0e0e0" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleComplete}
              size="sm"
              className={styles.action}
              variant="primary"
              disabled={loading}
            >
              {loading ? `Confirming` : `Confirm`}
            </Button>
          </div>
        </div>
      </Property.Body>
    </Property.Dialog>
  );
};

/** ALL COMPONENTS EXCEPT [BUTTON, MEDIA] */

const BrandComponentProperty = ({ widgets, onCancel, type }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const selectedWidgets = useSelectedWidgetSelector(editorSession);

  const storyboard = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboard, "type", "");

  const [state, setState] = useState({
    action: {
      type,
      value: '', // Random string for initial value
    },
    error: null,
  });
  const actionType = _.get(state.action, "type");
  const name = _.get(state.action, "value", "") || "Palette Element";

  const widget = _.size(widgets) > 1 ? selectedWidgets?.[0] : widgets[0];

  const uid = _.get(widget, "uid", "") || '';

  const editorBrandKit = useSelector((state) =>
    _.get(state, "brandKit.activated.createBrandElement", {})
  );
  const loading = _.get(editorBrandKit, `${uid}.loading`, false) || false;
  const error = _.get(editorBrandKit, `${uid}.error`, false) || false;
  const success = _.get(editorBrandKit, `${uid}.success`, false) || false;

  const targetSelector = _.size(widgets) === 1 ? `#${widgets[0].uid}` : '[data-able-grouptoolbarviewable="true"]';

  const brandKit = useSelector((state) =>_.get(state, "palette.brandkit", {}));
  const paletteData = (_.get(brandKit, "items", []) || []).find((item) => item.type === actionType);
  const brandkitId = _.get(paletteData, "id", null);

  useEffect(() => {
    if (!success) return;

    toast(
      <SuccessToast
        heading={`Success`}
        description={`Added to the Brand Kit Successfully.`}
      />,
      {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      }
    );

    dispatch(invalidateEditorBrand(uid));
    onCancel();
  }, [success]);

  useEffect(() => {
    if (!error) return;

    toast(
      <FailureToast
        heading={`Failure`}
        description={`Unable to Add it to the Brand Kit.`}
      />,
      {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      }
    );

    dispatch(invalidateEditorBrand(uid));
  }, [error]);

  const handleTypeChange = (type) => {
    setState(() => ({
      action: {
        type,
        value: "",
      },
      error: null,
    }));
  };

  const handleActionChange = (event) => {
    setState((prev) => ({
      ...prev,
      action: {
        ...prev.action,
        value: event.target.value,
      },
    }));
  };

  const handleCancel = () => {
    setState((state) => {
      return {
        ...state,
        action: {
          type: "FORMS",
          value: "",
        },
        error: null,
      };
    });
    onCancel();
  };

  const handleSave = () => {
    if (!state.action.value) {
      setState((prev) => ({
        ...prev,
        error: "Please enter a name.",
      }));
      return;
    }

    const payload = {
      uid,
      brandkitId,
      selectedWidgets,
      editorSession,
      name,
      themes,
    };

    dispatch(createEditorBrandComponent(payload));
  };

  return (
    <Property.Dialog
      className={styles.styleDialog}
      width={224}
      target={targetSelector}
      title={"Add to Brand Kit"}
      onCancel={handleCancel}
    >
      <Property.Body>
        <div className={styles.brandMediaContainer}>
          <div className={styles.componentActionsWrapper}>
            {optionFields.map((data) => {
              if((storyboardType === 'SPATIAL') && data.type === 'SEEK') return null;

              if(PAGE_CONTAIN_STORYBOARDS.includes(storyboardType) && data.type === 'MENUS') return null;

              return (
              <Accordion
                activeKey={actionType}
                defaultActiveKey={actionType}
                className={styles.componentActions}
              >
                <ComponentActions
                  label={data.label}
                  type={data.type}
                  handleChange={handleActionChange}
                  state={state}
                  handleTypeChange={handleTypeChange}
                />
              </Accordion>
              )})}
          </div>
          <div className={styles.actions}>
            <Button
              size="sm"
              className={styles.action}
              variant="secondary"
              onClick={handleCancel}
              style={{ borderColor: "#e0e0e0" }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              size="sm"
              className={styles.action}
              variant="primary"
              disabled={loading}
            >
              {loading ? `Saving` : `Save Changes`}
            </Button>
          </div>
        </div>
      </Property.Body>
    </Property.Dialog>
  );
};

const ComponentActions = ({
  label,
  type,
  state,
  handleTypeChange,
  handleChange,
}) => {
  const actionValue = _.get(state.action, "value") || "";

  return (
    <div className={styles.action}>
      <ActionHeader eventKey={type} onClick={() => handleTypeChange(type)}>
        {label}
      </ActionHeader>
      <Accordion.Collapse eventKey={type}>
        <Property
          name="What do you want to call this Element?"
          className={styles.linkProperty}
        >
          <Property.Input
            value={actionValue}
            onChange={handleChange}
            placeholder="Enter a name"
            error={state.error}
          />
        </Property>
      </Accordion.Collapse>
    </div>
  );
};

const ActionHeader = ({ eventKey, onClick, children }) => {
  const { activeEventKey } = React.useContext(AccordionContext);

  const decoratedOnClick = useAccordionButton(
    eventKey,
    () => onClick && onClick(eventKey)
  );

  const active = activeEventKey === eventKey;

  return (
    <div
      className={cx(styles.header, { [styles.active]: active })}
      onClick={decoratedOnClick}
    >
      <input type="radio" defaultChecked={active}></input>
      <label>{children}</label>
    </div>
  );
};

export {
  BrandMediaProperty,
  BrandButtonProperty,
  BrandComponentProperty,
};
